import { DownOutlined, LeftOutlined, SearchOutlined } from '@ant-design/icons'
import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import { getIncomingRenewalColumns } from '@components/charts/membership/analytics-overview/incomingRenewalColumns'
import { RenewalRateRing } from '@components/charts/membership/analytics-overview/RenewalRateGauge'
import {
  figureValue,
  formatInteger,
  formatPercent,
  lines,
  MIXED_CURRENCIES_CAPTION,
} from '@components/charts/membership/revenue/figures'
import InfoTip from '@components/charts/analytics/InfoTip'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import useResolveProgramToPlans from '@hooks/useResolveProgramToPlans'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import {
  FetchMembershipAnalyticsStatisticsParams,
  ResponseMembershipIncomingRenewalsAnalyticsStatistics,
} from '@models/membership/MembershipAnalyticsStatistics'
import { MembershipIncomingRenewal } from '@models/membership/MembershipIncomingRenewal'
import { fetchAnalyticsStatistics } from '@services/AnalyticsStatistics'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getMembershipIncomingRenewals,
  selectMembershipIncomingRenewals,
  setMembershipIncomingRenewalQuery,
} from '@store/membership/MembershipIncomingRenewalReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { renewalRangePresets } from '@utils/membershipRangePresets'
import { Permission } from '@utils/permissions'
import {
  dateRangeFromFilters,
  dateRangeToFilters,
} from '@utils/tableDateRangeFilter'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const { RangePicker } = DatePicker
const { Text } = Typography

/**
 * The API lists renewals over +/-365 days by default and rejects a range over
 * 3 years, so the page always sends an explicit, bounded one: the URL's range
 * when it has one (clamped to 3 years), else a year either side of today. The
 * tiles use the same range, so they describe the same rows as the table, under
 * the same filters (program, plans, payment, outcome, dormant, member search),
 * past and future due dates alike.
 */
const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_SPAN_DAYS = 365
const MAX_SPAN_YEARS = 3

const boundedDateFilters = (filters: Record<string, any>) => {
  const rawStart = filters.startDate?.[0] as string | undefined
  const rawEnd = filters.endDate?.[0] as string | undefined

  let start = rawStart
    ? dayjs(rawStart)
    : rawEnd
      ? dayjs(rawEnd).subtract(DEFAULT_SPAN_DAYS, 'day')
      : dayjs().subtract(DEFAULT_SPAN_DAYS, 'day')
  const end = rawEnd
    ? dayjs(rawEnd)
    : rawStart
      ? start.add(DEFAULT_SPAN_DAYS, 'day')
      : dayjs().add(DEFAULT_SPAN_DAYS, 'day')

  // Over 3 years: keep the end (the most recent rows) and move the start.
  const earliestStart = end.subtract(MAX_SPAN_YEARS, 'year').add(1, 'day')
  if (start.isBefore(earliestStart)) start = earliestStart

  return {
    ...filters,
    startDate: [start.format(DATE_FORMAT)],
    endDate: [end.format(DATE_FORMAT)],
  }
}

const PAYMENT_OPTIONS = [
  { value: 'card', label: 'Card' },
  { value: 'direct_debit', label: 'Direct debit' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'missing', label: 'Missing' },
  { value: 'unknown', label: 'Unknown' },
]

const ACTIVITY_OPTIONS = [
  { value: 'all', label: 'All members' },
  { value: 'dormant', label: 'Dormant members' },
]

const OUTCOME_OPTIONS = [
  { value: 'renewed', label: 'Renewed' },
  { value: 'payment_failed', label: 'Payment failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'awaiting_transfer', label: 'Awaiting transfer' },
  { value: 'upcoming', label: 'Upcoming' },
]

/**
 * Translates the table's own filters into what
 * `analytics/statistics?entity=membership` accepts, so the tiles aggregate
 * exactly the rows the table lists - the program id, plan ids and member
 * search go through untouched.
 */
const buildKpiParams = (
  query: TableParams
): FetchMembershipAnalyticsStatisticsParams => {
  // `TableParams['filters']` unions in the activity-log shape, which has none of
  // these keys; this table only ever passes the AntD record.
  const filters = query.filters as
    | Record<string, (string | number)[] | null>
    | undefined

  // Plans drive the scope; `programs` is only present on links that predate the
  // single Program / plan tree (Revenue page, old bookmarks) and keeps working.
  const programIds = (filters?.programs ?? []).map(Number)
  const planIds = (filters?.plans ?? []).map(Number)
  const member = query.search?.member ? String(query.search.member) : undefined
  // Forwarded so the tiles match the table when these filters are active.
  const paymentMethods = (filters?.paymentMethod ?? []).map(String)
  const outcomes = (filters?.outcome ?? []).map(String)
  const dormant = (filters?.dormant ?? []).map(String)

  const start = filters?.startDate?.[0]
  const end = filters?.endDate?.[0]

  return {
    entity: 'membership',
    kind: 'membership-incoming-renewals',
    startDate: String(start),
    endDate: String(end),
    programIds: programIds.length ? programIds : undefined,
    planIds: planIds.length ? planIds : undefined,
    filters:
      paymentMethods.length || outcomes.length || dormant.length
        ? {
            ...(paymentMethods.length && { paymentMethod: paymentMethods }),
            ...(outcomes.length && { outcome: outcomes }),
            ...(dormant.length && { dormant }),
          }
        : undefined,
    search: member ? { member } : undefined,
  }
}

const IncomingRenewalsPage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const renewals = useAppSelector(selectMembershipIncomingRenewals())
  const { query, isLoading } = useAppSelector(
    (state) => state.membershipIncomingRenewal
  )

  const [kpis, setKpis] =
    useState<ResponseMembershipIncomingRenewalsAnalyticsStatistics>()
  const [isKpisLoading, setIsKpisLoading] = useState<boolean>(false)
  const [kpisError, setKpisError] = useState<string>()

  // Only the newest request may write the tiles: filters change faster than the
  // aggregates come back, and a slower earlier response landing last used to
  // repaint the previous filter's figures over the current ones.
  const latestKpiRequest = useRef(0)

  const fetchKpis = async (newQuery: TableParams) => {
    const requestId = ++latestKpiRequest.current
    setIsKpisLoading(true)
    setKpisError(undefined)

    try {
      const { data } =
        await fetchAnalyticsStatistics<ResponseMembershipIncomingRenewalsAnalyticsStatistics>(
          buildKpiParams(newQuery)
        )

      if (requestId !== latestKpiRequest.current) return
      setKpis(data)
    } catch (error) {
      if (requestId !== latestKpiRequest.current) return

      console.error('Error fetching incoming renewal KPIs:', error)
      setKpisError('Failed to fetch data.')
      // Dropped rather than kept: an error message next to the previous
      // filter's numbers reads as if those numbers were still current.
      setKpis(undefined)
    } finally {
      if (requestId === latestKpiRequest.current) setIsKpisLoading(false)
    }
  }

  // Drives every load, the initial one included: `useTableUrlSync` calls this
  // back from an effect on the URL params, which runs on mount.
  const fetchRenewals = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      columnKey: params.sort.columnKey ?? 'date',
      order: params.sort.order ?? 'ascend',
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: boundedDateFilters(params.filters),
      search: params.search,
    }

    dispatch(setMembershipIncomingRenewalQuery(newQuery))
    dispatch(getMembershipIncomingRenewals(newQuery))
    fetchKpis(newQuery)
  }

  const { handleFiltersChange, handleTableChange, params, setSearchParams } =
    useTableUrlSync<MembershipIncomingRenewal>({
      onParamsChange: fetchRenewals,
    })

  useResolveProgramToPlans(params.filters, setSearchParams)

  const hasActiveFilters =
    [
      'plans',
      'programs',
      'paymentMethod',
      'outcome',
      'dormant',
      'startDate',
      'endDate',
    ].some((key) => (params.filters[key] ?? []).length > 0) ||
    Boolean(params.search.member)

  // Sorting only here: this page wires `onChange`, the overview preview does not.
  const columns = getIncomingRenewalColumns({
    withSorting: true,
    withOutcome: true,
  })

  return (
    <Page permissions={Permission.viewRevenueMembership} noWrapWithCol>
      <Col span={24}>
        <Flex gap={16} align="center">
          <Button
            aria-label="Back to Revenue"
            icon={<LeftOutlined />}
            onClick={() => navigate('/membership/revenue')}
          />
          <PageTitle title="Renewals" level={4} style={{ margin: 0 }} />
        </Flex>
      </Col>
      <Col span={24}>
        <TableFilters
          wrapRows
          initialValues={{
            plans: params.filters.plans?.map(Number),
            paymentMethod: params.filters.paymentMethod,
            outcome: params.filters.outcome,
            // `f_dormant=1` (Revenue page links) shows as "Dormant members".
            activity: params.filters.dormant?.[0] ? 'dormant' : undefined,
            member: params.search.member,
            // Shows the range actually sent (URL's, or the +/-365 days default).
            dateRange: dateRangeFromFilters(boundedDateFilters(params.filters)),
          }}
          onValuesChange={(filters: any, search: any) => {
            const { dateRange, activity, ...otherFilters } = filters

            // The picker displays the default range the page sends; writing it
            // to the URL would turn a default into a user choice. Only a range
            // the user actually changed is kept.
            const picked = dateRangeToFilters(dateRange)
            const defaults = boundedDateFilters({})
            const urlHasRange = Boolean(
              params.filters.startDate?.length || params.filters.endDate?.length
            )
            const isUntouchedDefault =
              !urlHasRange &&
              picked.startDate?.[0] === defaults.startDate[0] &&
              picked.endDate?.[0] === defaults.endDate[0]

            handleFiltersChange(
              {
                ...otherFilters,
                ...(isUntouchedDefault ? {} : picked),
                // The Activity select maps onto `filters[dormant]`.
                ...(activity === 'dormant' && { dormant: ['1'] }),
              },
              search
            )
          }}
          // Figma 829-1570: no labels, placeholders inside the controls, and
          // the member search next to the date range on the right.
          left={[
            {
              name: 'plans',
              type: 'filter',
              style: { width: 200 },
              input: (
                // One tree for programs and plans: checking a program checks all
                // of its plans. `f_programs` in an incoming URL (links from the
                // Revenue page, old bookmarks) is still honoured by the fetch
                // below, it just is not drawn in this control.
                <SBAsyncSelect
                  multiple
                  overrideDisplay="tree"
                  treeSelectProps={{ maxTagCount: 'responsive' }}
                  placeholder="Program / plan"
                  entity={Entity.membershipPlan}
                  allowClear
                />
              ),
            },
            {
              name: 'paymentMethod',
              type: 'filter',
              style: { width: 160 },
              input: (
                <Select
                  options={PAYMENT_OPTIONS}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="Payment"
                  allowClear
                />
              ),
            },
            {
              name: 'outcome',
              type: 'filter',
              style: { width: 180 },
              input: (
                <Select
                  options={OUTCOME_OPTIONS}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="Outcome"
                  allowClear
                />
              ),
            },
            {
              name: 'activity',
              type: 'filter',
              style: { width: 210 },
              input: (
                <Select
                  options={ACTIVITY_OPTIONS}
                  placeholder="Activity"
                  allowClear
                  suffixIcon={
                    <Space size={6}>
                      <InfoTip title="Dormant = no restaurant reservation and no event check-in in the last 90 days" />
                      <DownOutlined />
                    </Space>
                  }
                />
              ),
            },
          ]}
          right={[
            {
              name: 'member',
              type: 'search',
              style: { width: 260, maxWidth: 260 },
              input: (
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Search member..."
                  allowClear
                />
              ),
            },
            {
              name: 'dateRange',
              type: 'filter',
              style: { width: 240, maxWidth: 240 },
              input: (
                <RangePicker
                  className="w-full"
                  placement="bottomRight"
                  presets={renewalRangePresets()}
                  // A cleared picker would hide the default range it falls back to.
                  allowClear={false}
                  // The API rejects ranges over 3 years: past that distance
                  // from the first picked date, days cannot be picked.
                  disabledDate={(current, info) =>
                    info.from
                      ? current.isAfter(
                          info.from
                            .add(MAX_SPAN_YEARS, 'year')
                            .subtract(1, 'day'),
                          'day'
                        ) ||
                        current.isBefore(
                          info.from
                            .subtract(MAX_SPAN_YEARS, 'year')
                            .add(1, 'day'),
                          'day'
                        )
                      : false
                  }
                />
              ),
            },
          ]}
        />
      </Col>
      {/* KPIs sit under the filters, which drive them (Figma 829-1588). */}
      <Col span={24}>
        <Row gutter={[24, 24]} align="stretch">
          <Col flex="1">
            <AnalyticsStatistic
              variant="tile"
              title="Total to collect"
              value={
                kpis &&
                (kpis.totalToCollect?.value == null
                  ? '—'
                  : analyticsCurrency(
                      kpis.totalToCollect.value,
                      true,
                      kpis.currency
                    ))
              }
              caption={
                kpis && kpis.totalToCollect?.value == null
                  ? MIXED_CURRENCIES_CAPTION
                  : undefined
              }
              isLoading={isKpisLoading}
            />
          </Col>
          <Col flex="1">
            <AnalyticsStatistic
              variant="tile"
              title="Subscriptions to renew"
              value={kpis?.totalToRenew?.value}
              isLoading={isKpisLoading}
            />
          </Col>
          <Col flex="1">
            <AnalyticsStatistic
              variant="tile"
              title="Without saved card"
              tooltip="Card renewals due in the next 30 days with no card saved at Adyen: they will fail unless the member adds one."
              // `null` count: Adyen could not be queried. Never "0 at risk".
              value={
                kpis?.withoutPaymentMethod
                  ? kpis.withoutPaymentMethod.count === null
                    ? '—'
                    : formatInteger(kpis.withoutPaymentMethod.count)
                  : undefined
              }
              caption={
                kpis?.withoutPaymentMethod
                  ? kpis.withoutPaymentMethod.count === null
                    ? 'Could not check with Adyen'
                    : lines(
                        typeof kpis.withoutPaymentMethod.amount === 'number'
                          ? `${analyticsCurrency(kpis.withoutPaymentMethod.amount, false, kpis.currency)} at risk`
                          : kpis.withoutPaymentMethod.amount === null &&
                              MIXED_CURRENCIES_CAPTION,
                        // Some subscriptions could not be queried at Adyen:
                        // the count is a floor, say so.
                        (kpis.withoutPaymentMethod.unchecked ?? 0) > 0 &&
                          `${kpis.withoutPaymentMethod.unchecked} could not be checked with Adyen`
                      )
                  : undefined
              }
              isLoading={isKpisLoading}
            />
          </Col>
          {/* Only when the API returns them (range ending before today). */}
          {figureValue(kpis?.renewalRate) !== null && (
            <Col flex="1">
              <AnalyticsStatistic
                variant="tile"
                title="Renewal rate"
                tooltip="Share of the renewals due over the period that were renewed. Cancellations and unpaid transfers count as not renewed."
                value={formatPercent(figureValue(kpis?.renewalRate), 0)}
                tone="success"
                extra={
                  <RenewalRateRing percent={figureValue(kpis?.renewalRate)} />
                }
                isLoading={isKpisLoading}
              />
            </Col>
          )}
          {figureValue(kpis?.churn) !== null && (
            <Col flex="1">
              <AnalyticsStatistic
                variant="tile"
                title="Churn"
                tooltip="Share of the renewals due over the period that were not renewed."
                value={formatPercent(figureValue(kpis?.churn), 0)}
                tone="error"
                isLoading={isKpisLoading}
              />
            </Col>
          )}
        </Row>
        {kpisError && <Text type="danger">{kpisError}</Text>}
      </Col>
      <Col span={24}>
        {/* Rows and pager live inside one bordered card (Figma 829-2220). */}
        <Card
          style={{ borderRadius: 8 }}
          styles={{ body: { padding: 0, overflow: 'hidden' } }}
        >
          <SBTable
            columns={columns}
            dataSource={renewals}
            loading={isLoading}
            onChange={handleTableChange}
            pagination={{
              ...query.pagination,
              // Same footer as `TransactionsPage`: no size changer, count
              // pinned left, pager right.
              showSizeChanger: false,
              showTotal: (total, range) => (
                <span style={{ marginRight: 'auto' }}>
                  {`Showing ${range[0]}-${range[1]} of ${total}`}
                </span>
              ),
              style: {
                padding: 20,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            scroll={{ x: 'max-content' }}
            rowKey="id"
            empty={{
              description: hasActiveFilters
                ? 'No renewals match these filters'
                : 'No renewals found',
            }}
          />
        </Card>
      </Col>
    </Page>
  )
}

export default IncomingRenewalsPage
